import React, { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Typography, Paper } from '@material-ui/core';
import SchoolIcon from '@material-ui/icons/School';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { getThemeColors } from '../utils/themeUtils';

const useStyles = makeStyles(theme => ({
	root: {
		marginBottom: theme.spacing(2),
		marginTop: theme.spacing(1),
		width: '100%'
	},
	sectionTitleContainer: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		marginBottom: theme.spacing(2),
		marginTop: '15px'
	},
	sectionDivider: {
		flex: 1,
		height: '1px'
	},
	sectionDividerDark: {
		backgroundColor: '#334155'
	},
	sectionDividerLight: {
		backgroundColor: '#e2e8f0'
	},
	sectionTitle: {
		fontSize: '12px',
		fontWeight: 600,
		padding: `0 ${theme.spacing(2)}px`,
		whiteSpace: 'nowrap',
		textTransform: 'uppercase',
		letterSpacing: '0.5px'
	},
	sectionTitleDark: {
		color: '#64748b'
	},
	sectionTitleLight: {
		color: '#64748b'
	},
	scrollContainer: {
		display: 'flex',
		gap: theme.spacing(2),
		overflowX: 'auto',
		overflowY: 'hidden',
		paddingBottom: theme.spacing(1),
		scrollBehavior: 'smooth',
		scrollbarWidth: 'thin', // Firefox
		'&::-webkit-scrollbar': {
			height: 6
		},
		'&::-webkit-scrollbar-track': {
			borderRadius: 3
		},
		'&::-webkit-scrollbar-thumb': {
			borderRadius: 3
		}
	},
	scrollContainerDark: {
		scrollbarColor: '#334155 #0f172a', // thumb track (Firefox)
		'&::-webkit-scrollbar-track': {
			backgroundColor: '#0f172a'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: '#334155',
			'&:hover': {
				backgroundColor: '#475569'
			}
		}
	},
	scrollContainerLight: {
		scrollbarColor: '#cbd5e1 #f1f5f9',
		'&::-webkit-scrollbar-track': {
			backgroundColor: '#f1f5f9'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: '#cbd5e1',
			'&:hover': {
				backgroundColor: '#94a3b8'
			}
		}
	},
	courseCard: {
		minWidth: '320px',
		maxWidth: '320px',
		minHeight: '45px',
		borderRadius: '8px',
		padding: theme.spacing(1.5),
		paddingBottom: theme.spacing(5),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
		position: 'relative',
		transition: 'all 0.2s ease',
		cursor: 'pointer'
	},
	courseCardDark: {
		backgroundColor: '#1e293b',
		border: '1px solid #334155',
		'&:hover': {
			borderColor: '#475569',
			transform: 'translateY(-2px)',
			boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
		}
	},
	courseCardLight: {
		backgroundColor: '#ffffff',
		border: '1px solid #e2e8f0',
		'&:hover': {
			borderColor: '#cbd5e1',
			transform: 'translateY(-2px)',
			boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
		}
	},
	courseHeader: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(1)
	},
	iconContainer: {
		width: '32px',
		height: '32px',
		borderRadius: '6px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		backgroundColor: '#3b82f6',
		'& svg': {
			color: '#ffffff',
			fontSize: '18px'
		}
	},
	courseInfo: {
		flex: 1,
		minWidth: 0
	},
	courseTitle: {
		fontSize: '13px',
		fontWeight: 600,
		marginBottom: theme.spacing(0.3),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		whiteSpace: 'normal',
		lineHeight: 1.3,
		maxHeight: '34px'
	},
	courseTitleDark: {
		color: '#f8fafc'
	},
	courseTitleLight: {
		color: '#0f172a'
	},
	courseCategory: {
		fontSize: '10px',
		fontWeight: 500,
		padding: '2px 6px',
		borderRadius: '8px',
		display: 'inline-block',
		backgroundColor: '#1e293b',
		color: '#94a3b8'
	},
	statsGrid: {
		display: 'flex',
		gap: theme.spacing(1.5),
		flexShrink: 0
	},
	statItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	statLabel: {
		fontSize: '11px',
		color: '#64748b',
		fontWeight: 500
	},
	statValue: {
		fontSize: '12px',
		fontWeight: 600,
		color: '#f8fafc'
	},
	categoryBadge: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(1),
		fontSize: '9px',
		fontWeight: 600,
		padding: '2px 6px',
		borderRadius: '10px',
		backgroundColor: 'rgba(59, 130, 246, 0.2)',
		color: '#3b82f6',
		textTransform: 'uppercase',
		letterSpacing: '0.5px'
	},
	cardFooter: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		borderRadius: '0 0 8px 8px',
		padding: theme.spacing(1.2),
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	cardFooterDark: {
		backgroundColor: 'rgba(15, 23, 42, 0.5)',
		borderTop: '1px solid rgba(51, 65, 85, 0.5)'
	},
	cardFooterLight: {
		backgroundColor: '#f8fafc',
		borderTop: '1px solid #e2e8f0'
	},
	categoriesList: {
		display: 'flex',
		gap: theme.spacing(0.5),
		flexWrap: 'wrap',
		flex: 1,
		alignItems: 'center'
	},
	categoryTag: {
		fontSize: '9px',
		fontWeight: 600,
		padding: '2px 6px',
		borderRadius: '8px',
		textTransform: 'uppercase',
		letterSpacing: '0.3px'
	},
	categoryTagDark: {
		backgroundColor: 'rgba(59, 130, 246, 0.2)',
		color: '#3b82f6'
	},
	categoryTagLight: {
		backgroundColor: 'rgba(59, 130, 246, 0.15)',
		color: '#2563eb'
	},
	viewMoreButton: {
		fontSize: '11px',
		fontWeight: 600,
		color: '#3b82f6',
		textTransform: 'none',
		letterSpacing: '0.3px',
		cursor: 'pointer',
		transition: 'color 0.2s ease',
		'&:hover': {
			color: '#60a5fa'
		}
	}
}));

const CoursesStatsSection = ({ courses, themeMode = 'dark' }) => {
	const classes = useStyles();
	const theme = useTheme();
	const colors = getThemeColors(themeMode);
	const scrollContainerRef = useRef(null);
	const history = useHistory();

	// Infinite auto-scroll effect
	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		let scrollInterval;
		const scrollSpeed = 0.5; // pixels per interval

		const startScroll = () => {
			scrollInterval = setInterval(() => {
				if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
					// Reset to beginning for infinite loop
					container.scrollLeft = 0;
				} else {
					container.scrollLeft += scrollSpeed;
				}
			}, 20); // Update every 20ms for smooth animation
		};

		// Pause on hover
		const handleMouseEnter = () => {
			clearInterval(scrollInterval);
		};

		const handleMouseLeave = () => {
			startScroll();
		};

		container.addEventListener('mouseenter', handleMouseEnter);
		container.addEventListener('mouseleave', handleMouseLeave);

		// Start auto-scroll
		startScroll();

		// Cleanup
		return () => {
			clearInterval(scrollInterval);
			if (container) {
				container.removeEventListener('mouseenter', handleMouseEnter);
				container.removeEventListener('mouseleave', handleMouseLeave);
			}
		};
	}, [courses]);

	// Sort courses by category or name
	const sortedCourses = courses && courses.length > 0 
		? [...courses].sort((a, b) => {
			// Sort by category first, then by name
			if (a.category !== b.category) {
				return (a.category || '').localeCompare(b.category || '');
			}
			return (a.name || a.title || '').localeCompare(b.name || b.title || '');
		})
		: [];

	const handleCourseClick = (course) => {
		// Navigate to course detail page
		history.push(`/courses/${course.id}`);
	};

	return (
		<Box className={classes.root}>
		<Box className={classes.sectionTitleContainer}>
			<Box className={`${classes.sectionDivider} ${themeMode === 'dark' ? classes.sectionDividerDark : classes.sectionDividerLight}`} />
			<Typography className={`${classes.sectionTitle} ${themeMode === 'dark' ? classes.sectionTitleDark : classes.sectionTitleLight}`}>
				Courses Available
			</Typography>
			<Box className={`${classes.sectionDivider} ${themeMode === 'dark' ? classes.sectionDividerDark : classes.sectionDividerLight}`} />
		</Box>			{sortedCourses.length === 0 ? (
				<Typography style={{ fontSize: '14px', color: colors.text.tertiary, fontStyle: 'italic' }}>
					No courses available
				</Typography>
			) : (
				<Box
					ref={scrollContainerRef}
					className={`${classes.scrollContainer} ${themeMode === 'dark' ? classes.scrollContainerDark : classes.scrollContainerLight}`}
				>
				{sortedCourses.map((course, index) => (
					<Paper 
						key={course.id || course._id || index} 
						className={`${classes.courseCard} ${themeMode === 'dark' ? classes.courseCardDark : classes.courseCardLight}`}
						elevation={0}
						onClick={() => handleCourseClick(course)}
					>
						{/* Main Content Section */}
						<Box className={classes.courseHeader}>
							<Box className={classes.iconContainer} style={{ backgroundColor: colors.accent.blue }}>
								<SchoolIcon />
							</Box>
						<Box className={classes.courseInfo}>
							<Typography className={`${classes.courseTitle} ${themeMode === 'dark' ? classes.courseTitleDark : classes.courseTitleLight}`} title={course.name || course.title}>
								{course.name || course.title}
							</Typography>
						</Box>
						</Box>

						{/* Footer Section */}
						<Box className={`${classes.cardFooter} ${themeMode === 'dark' ? classes.cardFooterDark : classes.cardFooterLight}`}>
							<Box className={classes.categoriesList}>
								{course.category && course.category.split('/').map(function(cat, idx) {
									return (
										<span 
											key={idx}
											className={`${classes.categoryTag} ${themeMode === 'dark' ? classes.categoryTagDark : classes.categoryTagLight}`}
										>
											{cat.trim()}
										</span>
									);
								})}
							</Box>
							<Typography className={classes.viewMoreButton} style={{ color: themeMode === 'dark' ? '#3b82f6' : '#2563eb' }}>
								View →
							</Typography>
						</Box>
					</Paper>
				))}
			</Box>
			)}
		</Box>
	);
};

// Memoize to prevent unnecessary re-renders when courses haven't changed
export default React.memo(CoursesStatsSection, (prevProps, nextProps) => {
	return prevProps.themeMode === nextProps.themeMode &&
		JSON.stringify(prevProps.courses) === JSON.stringify(nextProps.courses);
});
